import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { config } from '../../utils/config';
import BlogListContainer from './BlogListContainer';
import './Blog.css';

const blogDetailsApi = config.getFullApiUrl ? config.getFullApiUrl('/api/blog-details') : `${config.API_URL}/api/blog-details`;
const BLOG_DETAILS_SESSION_KEY = 'blog_details_submitted';
const HCAPTCHA_SITEKEY = process.env.REACT_APP_HCAPTCHA_SITEKEY || '';

function Blog() {
  const history = useHistory();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedBlogSlug, setSelectedBlogSlug] = useState(null);
  const [selectedBlogTitle, setSelectedBlogTitle] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', phone_number: '', company: '', country: '' });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [hcaptchaToken, setHcaptchaToken] = useState(null);
  const hcaptchaRef = useRef(null);

  useEffect(() => {
    if (modalOpen) {
      setHcaptchaToken(null);
      hcaptchaRef.current?.resetCaptcha();
    }
  }, [modalOpen]);

  const handleBlogClick = (slugOrId, title = '') => {
    if (sessionStorage.getItem(BLOG_DETAILS_SESSION_KEY)) {
      history.push(`/blog/${slugOrId}`);
      return;
    }
    openBlogModal(slugOrId, title);
  };

  const openBlogModal = (slugOrId, title = '') => {
    setSelectedBlogSlug(slugOrId);
    setSelectedBlogTitle(title || '');
    setFormData({ name: '', email: '', phone_number: '', company: '', country: '' });
    setFormError('');
    setModalOpen(true);
  };

  const closeBlogModal = () => {
    setModalOpen(false);
    setSelectedBlogSlug(null);
    setSelectedBlogTitle('');
    setFormError('');
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormError('');
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (HCAPTCHA_SITEKEY && !hcaptchaToken) {
      setFormError('Please complete the verification.');
      return;
    }
    setFormSubmitting(true);
    try {
      const payload = {
        ...formData,
        blog_slug: selectedBlogSlug,
        hcaptchaToken: hcaptchaToken || ''
      };
      const res = await fetch(blogDetailsApi, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        sessionStorage.setItem(BLOG_DETAILS_SESSION_KEY, '1');
        closeBlogModal();
        history.push(`/blog/${selectedBlogSlug}`);
      } else {
        setFormError(data.message || 'Failed to save. Please try again.');
        setHcaptchaToken(null);
        hcaptchaRef.current?.resetCaptcha();
      }
    } catch (err) {
      setFormError('Request failed. Please try again.');
      setHcaptchaToken(null);
      hcaptchaRef.current?.resetCaptcha();
    } finally {
      setFormSubmitting(false);
    }
  };

  return (
    <div className="blog-page">
      {modalOpen && (
        <div className="blog-details-overlay" onClick={closeBlogModal} role="presentation">
          <div className="blog-details-modal blog-details-modal-v2" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="blog-details-close" onClick={closeBlogModal} aria-label="Close">
              <span aria-hidden>×</span>
            </button>
            <h3 className="blog-details-modal-title">Access Blog Content</h3>
            <p className="blog-details-intro">
              Please provide your details to access: <span className="blog-details-intro-link">{selectedBlogTitle || 'this article'}</span>
            </p>
            <form onSubmit={handleFormSubmit} className="blog-details-form blog-details-form-grid">
              <label className="blog-details-label">
                Full Name <span className="blog-details-required">*</span>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  placeholder="Enter your full name"
                  maxLength={200}
                  className="blog-details-input"
                />
              </label>
              <label className="blog-details-label">
                Email Address <span className="blog-details-required">*</span>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  placeholder="Enter your email address"
                  maxLength={255}
                  className="blog-details-input"
                />
              </label>
              <label className="blog-details-label">
                Phone Number <span className="blog-details-required">*</span>
                <input
                  type="tel"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleFormChange}
                  placeholder="Enter your phone number"
                  maxLength={45}
                  className="blog-details-input"
                />
              </label>
              <label className="blog-details-label">
                Company/Organization <span className="blog-details-required">*</span>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleFormChange}
                  placeholder="Enter your company or organization"
                  maxLength={200}
                  className="blog-details-input"
                />
              </label>
              <label className="blog-details-label blog-details-label-full">
                Country <span className="blog-details-required">*</span>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleFormChange}
                  placeholder="Enter your country"
                  maxLength={200}
                  className="blog-details-input"
                />
              </label>
              {HCAPTCHA_SITEKEY && (
                <div className="blog-details-hcaptcha-wrap blog-details-label-full">
                  <HCaptcha
                    ref={hcaptchaRef}
                    sitekey={HCAPTCHA_SITEKEY}
                    onVerify={(token) => setHcaptchaToken(token)}
                    onExpire={() => setHcaptchaToken(null)}
                    onError={() => setHcaptchaToken(null)}
                  />
                </div>
              )}
              {formError && <p className="blog-details-error blog-details-error-full">{formError}</p>}
              <div className="blog-details-actions blog-details-actions-full">
                <button
                  type="submit"
                  className="blog-details-submit blog-details-submit-full"
                  disabled={formSubmitting || (Boolean(HCAPTCHA_SITEKEY) && !hcaptchaToken)}
                >
                  {formSubmitting ? 'Saving…' : 'Submit'}
                </button>
              </div>
            </form>
            <p className="blog-details-footer">Your information will be used to provide you with relevant content and updates.</p>
          </div>
        </div>
      )}

      <div className="blog-page-inner">
        <BlogListContainer onReadMore={handleBlogClick} />
      </div>
    </div>
  );
}

export default Blog;
